/**
 * "Add files…" for a clip anywhere outside the Upload page (Dax, 2026-10-05): a picker, then the chosen videos
 * join the clip's timeline (a single clip becomes a project) and its photos join its drop, sent through the
 * same machinery as a drop, with one line of progress while they go. The clip analyses again once they land.
 */
import { useRef, useState, type ReactNode } from 'react';
import { IMAGE_MIME_BY_EXT, VIDEO_MIME_BY_EXT, uploadKindOf } from '@studio/contracts';
import { filesFromInput } from '@/lib/drop-files';
import { entryKey, preparePhotos, sendEntries, uploadSpec } from '@/lib/send-drop';
import { queryClient, trpc, trpcClient } from '@/lib/trpc';
import { holdTabOpen } from '@/lib/upload';

interface Target { assetId: string; name: string }

export function useAddFiles(): { pick: (target: Target) => void; node: ReactNode } {
  const input = useRef<HTMLInputElement>(null);
  const target = useRef<Target | null>(null);
  const [state, setState] = useState<{ name: string; sent: number; total: number; failed: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (t: Target, picked: File[]) => {
    const release = holdTabOpen();
    setError(null);
    try {
      setState({ name: t.name, sent: 0, total: 0, failed: 0 });
      const files = await preparePhotos(picked.map((file) => ({ file, path: null })));
      const registered = await trpcClient.upload.append.mutate({ assetId: t.assetId, files: files.map(uploadSpec) });
      const sending = registered.entries.flatMap((entry, i) => (entry ? [{ entry, file: files[i]!.file }] : []));
      const total = sending.reduce((n, x) => n + x.file.size, 0);
      const sent = new Map<string, number>();
      let failed = 0;
      setState({ name: t.name, sent: 0, total, failed });
      const show = () => setState({ name: t.name, sent: [...sent.values()].reduce((n, b) => n + b, 0), total, failed });
      await queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() });
      await sendEntries(sending, {
        onProgress: (entry, bytes) => { sent.set(entryKey(entry), bytes); show(); },
        onFailed: () => { failed += 1; show(); },
      });
      if (failed) setError(`${failed} file${failed === 1 ? '' : 's'} did not send; add ${failed === 1 ? 'it' : 'them'} again`);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setState(null);
      release();
      await queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() });
    }
  };

  const node = (
    <>
      <input ref={input} type="file" hidden multiple
        accept={Object.values({ ...VIDEO_MIME_BY_EXT, ...IMAGE_MIME_BY_EXT }).join(',')}
        aria-label="Choose files to add to this clip"
        onChange={(e) => {
          const t = target.current;
          const picked = filesFromInput(e.target.files).map((f) => f.file).filter((f) => uploadKindOf(f.name) === 'video' || uploadKindOf(f.name) === 'image');
          e.target.value = '';
          if (t && picked.length) void run(t, picked);
        }} />
      {state && (
        <div className="card pad row add-files-status" role="status">
          <span className="sub">Adding to &quot;{state.name}&quot;</span>
          <progress className="upbar" value={state.sent} max={state.total || 1} aria-label="Upload progress" />
        </div>
      )}
      {error && <div className="card pad error" role="alert">{error}</div>}
    </>
  );
  return { pick: (t) => { target.current = t; input.current?.click(); }, node };
}
